import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import { logger } from '../utils/debug/logger.js';
import pdfConfigStore from '../common/stores/pdfConfigStore.js';

let downloadManager = null;
let isDownloadButtonVisible = false;
let pdfConfigListenersSetup = false;

/**
 * Affiche les boutons d'options quand l'éventail est dessiné
 */
function showOptionsButtons() {
    const optionsButtonsGroup = document.getElementById('options-buttons-group');
    if (optionsButtonsGroup && !isDownloadButtonVisible) {
        optionsButtonsGroup.style.display = 'flex';
        isDownloadButtonVisible = true;
        logger.ui.debug('✅ Boutons d\'options affichés');
    }
}

/**
 * Cache les boutons d'options
 */
function hideOptionsButtons() {
    const optionsButtonsGroup = document.getElementById('options-buttons-group');
    if (optionsButtonsGroup && isDownloadButtonVisible) {
        optionsButtonsGroup.style.display = 'none';
        isDownloadButtonVisible = false;
        logger.ui.debug('✅ Boutons d\'options masqués');
    }
}

/**
 * Initialise le DownloadManager de manière paresseuse
 */
async function initializeDownloadManager() {
    if (!downloadManager) {
        logger.ui.info('🔄 Chargement du DownloadManager...');
        try {
            const { DownloadManager, updateFilename } = await import('../common/downloadManager.js');
            const { default: rootPersonStore } = await import('../common/stores/rootPersonStore.js');
            
            // Initialiser avec le nom de la personne racine actuelle
            const rootPersonName = rootPersonStore.rootPersonName || '';
            downloadManager = new DownloadManager(rootPersonName);
            
            // Mettre à jour le nom de fichier
            if (rootPersonName) {
                try {
                    const fanOfText = __("geneafan.genealogical_fan_of") || 'Éventail généalogique de ';
                    const filename = (fanOfText +
                        rootPersonName +
                        " créé sur genealog.ie"
                    ).replace(/[|&;$%@"<>()+,]/g, "");
                    updateFilename(filename);
                } catch (error) {
                    logger.ui.warn('Erreur lors de la récupération de la traduction, utilisation du texte par défaut:', error);
                    const filename = ('Éventail généalogique de ' +
                        rootPersonName +
                        " créé sur genealog.ie"
                    ).replace(/[|&;$%@"<>()+,]/g, "");
                    updateFilename(filename);
                }
            }
            
            logger.ui.info('✅ DownloadManager chargé avec succès');
        } catch (error) {
            logger.ui.error('❌ Erreur lors du chargement du DownloadManager:', error);
            throw error;
        }
    }
    return downloadManager;
}

/**
 * Configure les écouteurs d'événements pour les boutons de téléchargement
 */
function setupDownloadEventListeners() {
    // Bouton PDF (toujours visible)
    document.getElementById('download-pdf-simple')?.addEventListener('click', async () => {
        const dm = await initializeDownloadManager();
        dm.exportToPdfWithWatermark();
    });

    // PNG transparent
    document.getElementById('download-png-transparency')?.addEventListener('click', async () => {
        const dm = await initializeDownloadManager();
        dm.exportToPng(true);
    });

    // PNG fond blanc
    document.getElementById('download-png-background')?.addEventListener('click', async () => {
        const dm = await initializeDownloadManager();
        dm.exportToPng(false);
    });

    // SVG
    document.getElementById('download-svg')?.addEventListener('click', async () => {
        const dm = await initializeDownloadManager();
        dm.exportToSvg();
    });
}

/**
 * Met à jour l'indicateur de dimensions du frame
 */
function updateDimensionIndicator() {
    const indicator = document.getElementById('pdfFrameDimensions');
    if (indicator) {
        indicator.textContent = pdfConfigStore.dimensionsLabel;
    }
}

/**
 * Synchronise l'UI avec les préférences stockées
 */
function syncPdfConfigUI() {
    const config = pdfConfigStore.getConfig;

    // Synchroniser le format de papier
    const formatRadio = document.getElementById(`paper-format-${config.paperFormat.toLowerCase()}`);
    if (formatRadio) {
        formatRadio.checked = true;
    }

    // Synchroniser l'orientation
    const orientationRadio = document.getElementById(`orientation-${config.orientation}`);
    if (orientationRadio) {
        orientationRadio.checked = true;
    }

    // Mettre à jour l'indicateur de dimensions
    updateDimensionIndicator();
}

/**
 * Configure les écouteurs pour les options de format PDF
 */
function setupPdfConfigListeners() {
    if (pdfConfigListenersSetup) return;

    // Écouteurs pour le format de papier
    document.querySelectorAll('input[name="paperFormat"]').forEach(radio => {
        radio.addEventListener('change', (e) => {
            pdfConfigStore.setPaperFormat(e.target.value);
            updateDimensionIndicator();
            logger.ui.debug(`📄 Format papier: ${e.target.value}`);
        });
    });

    // Écouteurs pour l'orientation
    document.querySelectorAll('input[name="paperOrientation"]').forEach(radio => {
        radio.addEventListener('change', (e) => {
            pdfConfigStore.setOrientation(e.target.value);
            updateDimensionIndicator();
            logger.ui.debug(`🔄 Orientation: ${e.target.value}`);
        });
    });

    pdfConfigListenersSetup = true;
    logger.ui.debug('✅ Écouteurs PDF config configurés');
}

/**
 * Initialise la gestion du bouton de téléchargement
 */
export function initializeDownloadButton() {
    logger.initialization.bootstrap.info('🚀 Initialisation du bouton de téléchargement');
    
    // S'abonner à l'événement de dessin de l'éventail
    storeEvents.subscribe(EVENTS.VISUALIZATIONS.FAN.DRAWN, () => {
        logger.ui.info('📊 Événement FAN.DRAWN reçu - affichage des boutons d\'options');
        showOptionsButtons();
    });

    // S'abonner au changement de racine pour masquer les boutons
    storeEvents.subscribe(EVENTS.ROOT.CHANGED, () => {
        logger.ui.debug('🔄 Changement de racine - masquage des boutons d\'options');
        hideOptionsButtons();
    });

    // S'abonner au chargement d'un nouveau GEDCOM pour masquer les boutons
    document.addEventListener('gedcomLoaded', () => {
        logger.ui.debug('📁 Nouveau GEDCOM chargé - masquage des boutons d\'options');
        hideOptionsButtons();
    });

    // Configurer les écouteurs sur l'offcanvas de téléchargement
    const downloadOffcanvas = document.getElementById('downloadOffcanvas');
    if (downloadOffcanvas) {
        // Configuration initiale (une seule fois)
        downloadOffcanvas.addEventListener('shown.bs.offcanvas', async () => {
            logger.ui.info('📂 Offcanvas de téléchargement ouvert');
            await initializeDownloadManager();
            setupDownloadEventListeners();
            setupPdfConfigListeners();
        }, { once: true });

        // Synchroniser l'UI à chaque ouverture
        downloadOffcanvas.addEventListener('show.bs.offcanvas', () => {
            syncPdfConfigUI();
        });
    }
}
